import { roundReturnMoney } from './returnMoney';

interface MoneyOrder {
  id: string;
  orderId?: string | number | null;
  currencyCode?: string | null;
  paymentCurrencyCode?: string | null;
  items: Array<{
    id?: string | null;
    lineItemId?: string | null;
    quantity?: number | null;
    price: number | null;
    total?: number | null;
  }>;
}

export interface ReturnMoneySnapshot {
  version: 1;
  source: 'SHOPIFY_PRESENTMENT';
  orderId: string;
  shopifyOrderId: string;
  currency: string;
  shopCurrency: string;
  taxesIncluded: boolean;
  capturedAt: string;
  orderTotals: {
    total: number;
    subtotal: number;
    tax: number;
    shippingTotal: number;
    discountTotal: number;
  };
  items: Array<{
    orderItemId: string;
    lineItemId: string;
    quantity: number;
    price: number;
    total: number;
  }>;
}

const code = (value?: string | null) => value?.trim().toUpperCase() || '';
const object = (value: unknown): value is Record<string, any> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value));
const gid = (value: unknown, kind: string) => {
  const text = String(value ?? '');
  return /^\d+$/.test(text) ? `gid://shopify/${kind}/${text}` : text;
};

export function isMixedReturnCurrency(
  order?: Pick<MoneyOrder, 'currencyCode' | 'paymentCurrencyCode'> | null,
  storeCurrency?: string | null,
) {
  const shopCurrency = code(order?.currencyCode) || code(storeCurrency);
  const paymentCurrency = code(order?.paymentCurrencyCode) || shopCurrency;
  return Boolean(paymentCurrency && paymentCurrency !== shopCurrency);
}

export function validateReturnMoneySnapshot(
  order: MoneyOrder,
  value: unknown,
  requireAllItems = true,
  storeCurrency?: string | null,
): ReturnMoneySnapshot | null {
  if (!object(value) || !isMixedReturnCurrency(order, storeCurrency))
    return null;
  const currency = code(order.paymentCurrencyCode);
  const validAmount = (amount: unknown): amount is number =>
    typeof amount === 'number' &&
    Number.isFinite(amount) &&
    amount >= 0 &&
    Math.abs(amount - roundReturnMoney(amount, currency)) <=
      Number.EPSILON * Math.max(1, Math.abs(amount)) * 4;
  if (
    value.version !== 1 ||
    value.source !== 'SHOPIFY_PRESENTMENT' ||
    value.orderId !== order.id ||
    value.shopifyOrderId !== gid(order.orderId, 'Order') ||
    !/^gid:\/\/shopify\/Order\/\d+$/.test(value.shopifyOrderId) ||
    value.currency !== currency ||
    value.shopCurrency !== (code(order.currencyCode) || code(storeCurrency)) ||
    typeof value.taxesIncluded !== 'boolean' ||
    typeof value.capturedAt !== 'string' ||
    !Number.isFinite(Date.parse(value.capturedAt))
  )
    return null;
  if (
    !object(value.orderTotals) ||
    !['total', 'subtotal', 'tax', 'shippingTotal', 'discountTotal'].every(
      (key) => validAmount(value.orderTotals[key]),
    )
  )
    return null;
  if (
    !Array.isArray(value.items) ||
    !value.items.length ||
    (requireAllItems && value.items.length !== order.items.length)
  )
    return null;
  const items = new Map<string, ReturnMoneySnapshot['items'][number]>();
  const lineIds = new Set<string>();
  for (const item of value.items) {
    if (
      !object(item) ||
      typeof item.orderItemId !== 'string' ||
      !item.orderItemId ||
      items.has(item.orderItemId) ||
      typeof item.lineItemId !== 'string' ||
      !/^gid:\/\/shopify\/LineItem\/\d+$/.test(item.lineItemId) ||
      lineIds.has(item.lineItemId) ||
      !Number.isSafeInteger(item.quantity) ||
      item.quantity < 0 ||
      !validAmount(item.price) ||
      !validAmount(item.total)
    )
      return null;
    items.set(item.orderItemId, item as ReturnMoneySnapshot['items'][number]);
    lineIds.add(item.lineItemId);
  }
  for (const item of order.items) {
    if (typeof item.id !== 'string' || !item.id) return null;
    const monetaryItem = items.get(item.id);
    if (
      !monetaryItem ||
      monetaryItem.quantity !== item.quantity ||
      (item.lineItemId != null &&
        monetaryItem.lineItemId !== gid(item.lineItemId, 'LineItem'))
    )
      return null;
  }
  return value as ReturnMoneySnapshot;
}

export function projectReturnOrderMoney<T extends MoneyOrder>(
  order: T,
  value: unknown,
  storeCurrency?: string | null,
): T | null {
  if (!isMixedReturnCurrency(order, storeCurrency)) return order;
  const snapshot = validateReturnMoneySnapshot(
    order,
    value,
    true,
    storeCurrency,
  );
  if (!snapshot) return null;
  const items = new Map(snapshot.items.map((item) => [item.orderItemId, item]));
  return {
    ...order,
    ...snapshot.orderTotals,
    items: order.items.map((item) => ({
      ...item,
      price: items.get(item.id!)!.price,
      total: items.get(item.id!)!.total,
    })),
  };
}
